/// <amd-module name="Pipeline/Analytics/TaskInsights/Sources/TaskInsightSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { IReportContext, ITrendReportConfiguration, TrendBy } from "Analytics/Shared/Common/Types";
import { getDateFilter } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { expr } from "Analytics/Shared/FilterExpression";
import { ODataQuery } from "Analytics/Shared/QueryBuilder";
import { PipelineReportSource } from "Pipeline/Analytics/Common/Sources/PipelineReportSource";
import { IVssPageContext } from "VSS/Platform/Context";
import * as Utils_String from "VSS/Core/Util/String";

export interface IPipelineTaskInsightData {
    totalPipelineRunsCount?: number;
    taskFailureCount?: number;
}

export class TaskInsightSource extends PipelineReportSource {
    constructor(_pageContext: IVssPageContext) {
        super("Pipelines_TaskInsights", _pageContext);
    }

    public async queryTotalPipelineRunsCount(
        reportContext: IReportContext,
        confValues: ITrendReportConfiguration,
        taskName: string
    ): Promise<IPipelineTaskInsightData> {
        const projectId = this._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        const query = new ODataQuery()
            .filter(
                expr(this._getPipelineContextFilter(reportContext, true))
                    .and.expr(getDateFilter(TrendBy.Days, confValues.period, `${PipelineEntities.PipelineRunCompletedDateSK}`))
                    .and.expr("TaskDisplayName eq '" + taskName + "'")
            )
            .groupBy(`${PipelineEntities.PipelineRunId}`)
            .aggregate("$count as FailedTaskCount")
            .aggregate("$count as TotalCount");

        queryOptions.$apply = query.apply;

        return this._queryTotalRunsData(queryOptions);
    }

    public async queryTotalTaskFailureCount(
        reportContext: IReportContext,
        confValues: ITrendReportConfiguration,
        taskName: string,
        jobId: string,
        phaseId: string,
        stageId: string
    ): Promise<IPipelineTaskInsightData> {
        const projectId = this._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        const queryJobId = stageId === this._defaultStage ? `${phaseId}.${jobId}` : `${stageId}.${phaseId}.${jobId}`;
        const query = new ODataQuery()
            .filter(
                expr(this._getPipelineContextFilter(reportContext, true))
                    .and.expr(getDateFilter(TrendBy.Days, confValues.period, `${PipelineEntities.PipelineRunCompletedDateSK}`))
                    .and.expr("PipelineJob/StageId eq '" + stageId + "'")
                    .and.expr("PipelineJob/JobId eq '" + `${queryJobId}` + "'")
                    .and.expr("TaskDisplayName eq '" + taskName + "'")
                    .and.expr("TaskOutcome eq 'Failed'")
            )
            .aggregate("$count as TotalCount");

        queryOptions.$apply = query.apply;

        return this._queryFailureCountData(queryOptions);
    }

    private async _queryFailureCountData(queryOptions: ODataQueryOptions): Promise<IPipelineTaskInsightData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }

        let failureCountData: IPipelineTaskInsightData = {};
        failureCountData = {
            taskFailureCount: data.value[0] && data.value[0].TotalCount
        };
        return failureCountData;
    }

    private async _queryTotalRunsData(queryOptions: ODataQueryOptions): Promise<IPipelineTaskInsightData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }

        let pipelineRunsCountData: IPipelineTaskInsightData = {};
        pipelineRunsCountData = {
            totalPipelineRunsCount: data.value[0] && data.value[0].TotalCount
        };
        return pipelineRunsCountData;
    }
}
